/**
 * The inbox queue the reviewer was working in, kept across the record step's detour.
 *
 * The record step leaves the inbox for the incident or complaint add page, which unmounts the
 * inbox and prunes its path state (a different module). Coming back used to re-run the smart
 * default: a reviewer working the All view or a filtered channel landed on Breaches, the next
 * message was missing from the list, and the pager and the next submit destination went with it.
 *
 * The inbox records its scope here as it changes. The add page's way back into the queue
 * (`useReviewQueueNavigation`) asks for the scope to be resumed, and only that return restores
 * it: any other entry into the inbox is a fresh queue and gets the smart default. The scope is
 * per organisation and held in memory for the tab, so a full reload of the add page falls back
 * to the smart default.
 */

import type { Filter } from '@client/components/filters';
import type { InboxFilterKey } from '@client/routes/~console.$orgId/~tools/~auto-detect/~inbox/InboxViewBar';
import type { RouterInputs } from '@client/trpc/client';

export type InboxChannel = 'ALL' | 'EMAIL' | 'SMS';

export interface InboxQueueScope {
    view: InboxFilterKey;
    channel: InboxChannel;
    /** Where, sort and page, as the table last requested them. */
    queryParams: RouterInputs['messageClassification']['listMessageClassification'];
    /** The filter bar's chips, so the restored where is not an invisible filter. */
    filters: Filter[];
}

const scopes = new Map<string, InboxQueueScope>();
const resumeRequested = new Set<string>();

export function saveInboxQueueScope(orgId: bigint, scope: InboxQueueScope): void {
    scopes.set(orgId.toString(), scope);
}

/** Called just before returning to the queue from a record form. */
export function requestInboxQueueResume(orgId: bigint): void {
    resumeRequested.add(orgId.toString());
}

/**
 * The scope to restore on this inbox mount, or null for a fresh queue. Has no side effect, so a
 * double-invoked state initialiser reads the same answer; `settleInboxQueueResume` consumes it.
 */
export function peekInboxQueueResume(orgId: bigint): InboxQueueScope | null {
    const key = orgId.toString();
    return resumeRequested.has(key) ? (scopes.get(key) ?? null) : null;
}

/** The inbox has mounted: a later entry is fresh unless another return asks again. */
export function settleInboxQueueResume(orgId: bigint): void {
    resumeRequested.delete(orgId.toString());
}

/** Test seam: forget every scope and pending resume. */
export function resetInboxQueueScopes(): void {
    scopes.clear();
    resumeRequested.clear();
}
